import React from 'react';
import { Stack, Spinner, ToggleButtonGroup, ToggleButton, Row, Col, Form } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import Totals from './Totals.js';
import SelectMetric from './SelectMetric.js';
import SelectSplitMetric from './SelectSplitMetric.js';
import { DataContext } from './DataContextProvider.js';
import COLORS from './colors.js';

const metricNames = ['users', 'sessions', 'page_views', 'organic_views', 'direct_views', 'paid_views', 'signups', 'rate'];
const maxSplitBars = 6;
let chartJsLabelFix = false;

export default function LineChart({ filters, withTotals, withSplits, fixedMetric, fixedSplit, fixedStart, fixedEnd, fixedInterval, fixedData, fixedStackBars, fixedSeries, title }) {
  const [selectedMetric, setSelectedMetric] = React.useState(-1);
  const [stackBars, setStackBars] = React.useState(true);
  const dataContext = React.useContext(DataContext);
  const dataAvailable = fixedData ? true : dataContext.getDateData({
    start: (fixedStart || dataContext.start),
    end: (fixedEnd || dataContext.end),
    interval: (fixedInterval || dataContext.interval),
    filters: filters,
    split: withSplits ? (fixedSplit || dataContext.split) : '',
  });
  const allData = fixedData ? fixedData : (dataAvailable ? dataContext.dateData.current.results : undefined);
  let data = fixedSeries ? allData[0] : allData;

  const getTotalsLabels = () => data.map(x => x[0]);
  const getTotalsDatasets = () => {
    const metric = (!!fixedMetric) ? metricNames.indexOf(fixedMetric) + 1 : selectedMetric;
    return [{
      backgroundColor: COLORS[0],
      borderColor: COLORS[0],
      data: data.map(x => x[metric]),
      trendlineLinear: {
        style: "rgba(255, 99, 132, 0.5)",
        lineStyle: "solid",
        width: 4,
      },
    }];
  };

  const getSplitLabels = () => {
    if (dataContext.split === 'source') {
      // treat traffic sources as a special case
      return getTotalsLabels();
    }
    const labels = [];
    for (let i = 0; i < data.length; i++) {
      if (labels.length === 0 || labels[labels.length - 1] !== data[i][0][0]) {
        labels.push(data[i][0][0]);
      }
    }
    return labels;
  };
  const getSplitDatasets = () => {
    if (dataContext.split === 'source') {
      // treat traffic sources as a special case
      const stacked = fixedStackBars !== undefined ? fixedStackBars : stackBars;
      const datasets = [];
      const views = metricNames.indexOf('page_views') + 1;
      const organic = metricNames.indexOf('organic_views') + 1;
      const direct = metricNames.indexOf('direct_views') + 1;
      const paid = metricNames.indexOf('paid_views') + 1;
      datasets.push({
        backgroundColor: COLORS[0],
        borderColor: COLORS[0],
        data: data.map(x => x[organic]),
        label: 'Organic' + ' '.repeat(chartJsLabelFix),
        stack: stacked ? 'bar' : undefined,
      });
      datasets.push({
        backgroundColor: COLORS[1],
        borderColor: COLORS[1],
        data: data.map(x => x[direct]),
        label: 'Direct' + ' '.repeat(chartJsLabelFix),
        stack: stacked ? 'bar' : undefined,
      });
      datasets.push({
        backgroundColor: COLORS[2],
        borderColor: COLORS[2],
        data: data.map(x => x[paid]),
        label: 'Paid' + ' '.repeat(chartJsLabelFix),
        stack: stacked ? 'bar' : undefined,
      });
      datasets.push({
        backgroundColor: COLORS[3],
        borderColor: COLORS[3],
        data: data.map(x => x[views] - x[organic] - x[direct] - x[paid]),
        label: 'Other' + ' '.repeat(chartJsLabelFix),
        stack: stacked ? 'bar' : undefined,
      });
      chartJsLabelFix = (chartJsLabelFix + 1) % 3;
      return datasets;
    }
    const metric = (!!fixedMetric) ? metricNames.indexOf(fixedMetric) + 1 : selectedMetric;
    const series = {};
    let n = 0;
    for (let i = 0; i < data.length; i++) {
      const date = data[i][0][0];
      if (i > 0 && date !== data[i - 1][0][0]) {
        n++;
        for (let j in series) {
          while (series[j].length < n) {
            series[j].push(0);
          }
        }
      }
      const category = data[i][0][1];
      if (!(category in series)) {
        series[category] = new Array(n).fill(0);
      }
      series[category].push(data[i][metric]);
    }
    n++;
    for (let j in series) {
      while (series[j].length < n) {
        series[j].push(0);
      }
    }
    let totals = Object.keys(series).map(category => [category, series[category].reduce((a, b) => a + b)]);
    totals.sort((a, b) => b[1] - a[1]);
    const stacked = fixedStackBars !== undefined ? fixedStackBars : stackBars;
    const datasets = [];
    for (let i = 0; i < Math.min(maxSplitBars - 1, totals.length); i++) {
      const category = totals[i][0];
      datasets.push({
        backgroundColor: COLORS[i % COLORS.length],
        borderColor: COLORS[i % COLORS.length],
        data: series[category],
        label: category + ' '.repeat(chartJsLabelFix),
        stack: stacked ? 'bar' : undefined,
      });
    }
    if (totals.length >= maxSplitBars) {
      const all_others = new Array(n).fill(0);
      for (let i = maxSplitBars; i < totals.length; i++) {
        const category = totals[i][0];
        for (let j = 0; j < all_others.length; j++) {
          all_others[j] += series[category][j];
        }
      }
      datasets.push({
        backgroundColor: COLORS[datasets.length % COLORS.length],
        borderColor: COLORS[datasets.length % COLORS.length],
        data: all_others,
        label: 'All others' + ' '.repeat(chartJsLabelFix),
        stack: stacked ? 'bar' : undefined,
      });
    }
    chartJsLabelFix = (chartJsLabelFix + 1) % 3;
    return datasets;
  };

  let chartData = {}
  if (dataAvailable && (!!fixedMetric || selectedMetric !== -1)) {
    chartData.labels = (!withSplits || (!fixedSplit && !dataContext.split)) ? getTotalsLabels() : getSplitLabels();
    chartData.datasets = (!withSplits || (!fixedSplit && !dataContext.split)) ? getTotalsDatasets() : getSplitDatasets();

    if (!withSplits && fixedSeries) {
      chartData.datasets[0].label = fixedSeries[0];
      chartData.datasets[0].trendlineLinear = undefined;
      for (let i = 1; i < allData.length; i++) {
        data = allData[i];
        chartData.datasets.push(getTotalsDatasets()[0]);
        chartData.datasets[i].label = fixedSeries[i];
        chartData.datasets[i].trendlineLinear = undefined;
        chartData.datasets[i].backgroundColor = COLORS[i % COLORS.length];
        chartData.datasets[i].borderColor = COLORS[i % COLORS.length];
      }
    }
  }
  let options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !!(fixedSeries || (withSplits && (!!fixedSplit || !!dataContext.split))),
        position: fixedSeries ? 'top' : 'right',
        reverse: !fixedSeries
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const changeInterval = ev => dataContext.setInterval(ev.target.innerText);
  const toggleStackBars = () => setStackBars(!stackBars);

  return (
    <>
      {withTotals &&
        <Totals filters={filters} dataAvailable={dataAvailable} />
      }
      <div className="card line-chart">
        {title &&
          <Row><Col><h4>{title}</h4></Col></Row>
        }
        <Row>
          <Col className="select-metric">
            <Stack direction="horizontal">
              {!fixedMetric &&
                <SelectMetric changeMetric={setSelectedMetric} withCount={false} name="line" />
              }
              {withSplits && !fixedSplit && selectedMetric > 0 &&
                <>
                  <SelectSplitMetric metric={metricNames[selectedMetric - 1]} />
                  <Form.Check type="checkbox" size="sm" label="Stack" disabled={!dataContext.split} checked={stackBars} onClick={toggleStackBars}></Form.Check>
                </>
              }
            </Stack>
          </Col>
          {!fixedInterval &&
            <Col className="select-interval">
              <ToggleButtonGroup name="interval" type="radio" value={dataContext.interval} onClick={changeInterval}>
                <ToggleButton value={"d"} variant="outline-secondary">d</ToggleButton>
                <ToggleButton value={"w"} variant="outline-secondary">w</ToggleButton>
                <ToggleButton value={"m"} variant="outline-secondary">m</ToggleButton>
                <ToggleButton value={"q"} variant="outline-secondary">q</ToggleButton>
                <ToggleButton value={"y"} variant="outline-secondary">y</ToggleButton>
             </ToggleButtonGroup>
            </Col>
          }
        </Row>
        { (dataAvailable && chartData.labels) ?
          <div width="100%" height="200px">
            <Bar data={chartData} options={options} />
          </div>
        :
          <Spinner animation="grow" />
        }
      </div>
    </>
  );
}
